/**
 * SCRUM-169 — dashboard GA básico (Data API / demo).
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink, RefreshCw } from "lucide-react";
import { CrudShellHeader } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson } from "@/lib/sm-api";
import type { GaBasicReport, GaReportRange } from "@/lib/ga-provisioning/data-api";

export const Route = createFileRoute("/app/analytics-ga")({
  component: AnalyticsGaPage,
  head: () => ({ meta: [{ title: "Analytics GA4" }] }),
});

function AnalyticsGaPage() {
  const tid = useWorkspaceTenantId();
  const [range, setRange] = useState<GaReportRange>("7d");
  const [report, setReport] = useState<GaBasicReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consoleUrl, setConsoleUrl] = useState<string | null>(null);

  const load = async () => {
    if (!tid) return;
    setLoading(true);
    setError(null);
    try {
      const [rep, integ] = await Promise.all([
        apiGetJson<{ report?: GaBasicReport }>(`/api/analytics-ga-report?range=${range}`, tid),
        apiGetJson<{ propertyConsoleUrl?: string | null }>("/api/analytics-integration", tid).catch(() => ({})),
      ]);
      setReport(rep.report ?? null);
      setConsoleUrl(integ.propertyConsoleUrl ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid, range]);

  return (
    <div className="space-y-4 pb-8">
      <CrudShellHeader
        title="Analytics GA4"
        subtitle="Métricas de la Property activa del workspace"
        actions={
          <div className="flex flex-wrap gap-2">
            {(["7d", "28d", "90d"] as GaReportRange[]).map((r) => (
              <Button
                key={r}
                type="button"
                size="sm"
                variant={range === r ? "default" : "outline"}
                onClick={() => setRange(r)}
              >
                {r}
              </Button>
            ))}
            <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => void load()} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5" /> Actualizar
            </Button>
            <Button type="button" size="sm" variant="ghost" asChild>
              <Link to="/app/settings" search={{ tab: "tracking" } as never}>
                Configurar
              </Link>
            </Button>
          </div>
        }
      />

      {error ? <p className="text-sm text-destructive px-1">{error}</p> : null}

      {loading && !report ? (
        <p className="text-sm text-muted-foreground px-1">Cargando…</p>
      ) : report ? (
        <div className="space-y-4 px-1">
          <div className="rounded-xl border border-border/60 bg-card p-4 text-sm">
            <p>
              Modo: <span className="font-medium">{report.mode ?? "—"}</span>
              {report.measurementId ? (
                <> · ID <span className="font-mono text-xs">{report.measurementId}</span></>
              ) : null}
            </p>
            {report.unavailableReason ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {report.unavailableReason === "demo_data_no_credentials"
                  ? "Datos demo (sin credenciales GA_ADMIN_* en el entorno)."
                  : report.unavailableReason}
              </p>
            ) : null}
            {consoleUrl ? (
              <a
                href={consoleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Abrir en Google Analytics <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ["Usuarios activos", report.activeUsers],
              ["Sesiones", report.sessions],
              ["Vistas", report.screenPageViews],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-xl border border-border/60 bg-card p-4">
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border/60 bg-card p-4">
            <p className="text-sm font-medium mb-2">Fuentes principales</p>
            {report.topSources.length === 0 ? (
              <p className="text-xs text-muted-foreground">Sin datos de adquisición.</p>
            ) : (
              <ul className="space-y-1.5">
                {report.topSources.map((s) => (
                  <li key={s.source} className="flex justify-between text-sm">
                    <span className="truncate">{s.source}</span>
                    <span className="tabular-nums text-muted-foreground">{s.sessions}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
